<template>
  <div v-show="isActive" class="widgetContainer" id="measureID">

    <MapToolHeader 
      title="Medir" 
      :isHidden.sync="isHidden" 
      headerId="measureIDheader"
      @close="exitMeasure()"
    />

    <div class="tool-body" v-show="!isHidden">
      <!-- Selector de Modos (Botones) -->
      <div class="mode-selector">
        <button 
          class="mode-btn" 
          :class="{ active: mode.code === 'area' }"
          @click="setMode('area')"
          title="Medir Área"
        >
          <font-awesome-icon icon="draw-polygon" />
        </button>
        <button 
          class="mode-btn" 
          :class="{ active: mode.code === 'distance' }"
          @click="setMode('distance')"
          title="Medir Distancia"
        >
          <font-awesome-icon icon="ruler-combined" />
        </button>
        <button 
          class="mode-btn" 
          :class="{ active: mode.code === 'radius' }"
          @click="setMode('radius')"
          title="Medir Radio/Ubicación"
        >
          <font-awesome-icon icon="dot-circle" />
        </button>
        <button 
          class="mode-btn" 
          :class="{ active: mode.code === 'coords' }"
          @click="setMode('coords')"
          title="Obtener Coordenadas"
        >
          <font-awesome-icon icon="map-marker-alt" />
        </button>
      </div>

      <!-- Selector de Unidades / Formato Coordenadas -->
      <div class="unit-selector" v-if="mode.code">
        <select v-if="mode.code === 'coords'" v-model="coordFormat" class="custom-select">
          <option value="GMS">GMS</option>
          <option value="Decimal">Decimal</option>
          <option value="UTM">UTM</option>
        </select>
        <select v-else v-model="selectedUnit" class="custom-select">
          <option v-for="unit in availableUnits" :key="unit.value" :value="unit.value">
            {{ unit.label }}
          </option>
        </select>
      </div>

      <!-- Resultados Estándar -->
      <div class="results-container" v-if="mode.code !== 'coords'">
        <p class="results-label">Resultado de la medición</p>
        <p class="results-value" v-if="formattedResult">
          <span v-if="mode.code === 'radius'" class="results-label">Radio</span>{{ formattedResult }}
        </p>
        <p class="results-placeholder" v-else>Comience a dibujar en el mapa</p>
        <p class="results-area" v-if="formattedArea">Área: {{ formattedArea }}</p>
      </div>

      <!-- Resultados Coordenadas (Estilo especial) -->
      <div class="coords-results-container" v-if="mode.code === 'coords'">
        <p class="results-label">Resultado de la medición</p>
        <div class="coords-table-wrapper">
          <table class="coords-table">
            <thead>
              <tr>
                <th style="width: 30px;"></th>
                <th>{{ coordFormat === 'UTM' ? 'Este' : 'Latitud' }}</th>
                <th>{{ coordFormat === 'UTM' ? 'Norte' : 'Longitud' }}</th>
                <th v-if="coordFormat === 'UTM'" style="width: 50px;">Zona</th>
              </tr>
            </thead>
            <tbody>
              <!-- Fila 1: Puntero (Tiempo Real) -->
              <tr>
                <td><font-awesome-icon icon="mouse-pointer" style="color: #64748b; font-size: 0.8rem;" /></td>
                <td class="coord-val">{{ coordCell(pointerPoint, 0) }}</td>
                <td class="coord-val">{{ coordCell(pointerPoint, 1) }}</td>
                <td v-if="coordFormat === 'UTM'" class="coord-val">{{ coordZone(pointerPoint) }}</td>
              </tr>
              <!-- Fila 2: Punto (Fijo tras clic) -->
              <tr>
                <td><font-awesome-icon icon="map-marker-alt" style="color: #27ae60; font-size: 0.9rem;" /></td>
                <td class="coord-val">{{ coordCell(fixedPoint, 0) }}</td>
                <td class="coord-val">{{ coordCell(fixedPoint, 1) }}</td>
                <td v-if="coordFormat === 'UTM'" class="coord-val">{{ coordZone(fixedPoint) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn-clean" @click="cleanMode()">
          Limpiar <font-awesome-icon icon="trash-alt" />
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import {draggableDiv} from '@/components/mixin/draggableDiv.js'
import {vuex} from '@/components/mixin/vuex.js'
import { mapService } from '@/services/map/mapService'

import { measureDistance } from './measureDistance.js'
import { measureArea } from './measureArea.js'
import { measureRadius } from './measureRadius.js'
import { measureLocation } from './measureLocation.js'
import MapToolHeader from '@/components/common/MapToolHeader.vue'

// En modo radio el selector ofrece unidades lineales; el área se muestra en la unidad
// cuadrada equivalente. Millas náuticas no tienen par en getConvertedValue: caen a m².
const RADIUS_AREA_UNITS = { m: 'm2', km: 'km2', mi: 'mi2', ft: 'ft2', yd: 'yd2', nmi: 'm2' };

export default {
  name: 'Measure',
  props: ['isFixed', 'currentMode'],
  mixins:[vuex, draggableDiv, measureDistance, measureArea, measureRadius, measureLocation],
  data() {
    return {
      options: [
        { name: '[Seleccionar modo]', code: ''},
        { name: 'Distancia', code: 'distance'},
        { name: 'Área', code: 'area'},
        { name: 'Radio', code: 'radius'},
        { name: 'Coordenadas', code: 'coords'}
      ],
      mode: { name: '[Seleccionar modo]', code: ''},
      coordFormat: 'GMS', // 'GMS', 'Decimal' o 'UTM'
      selectedUnit: '',
      rawMeasureValue: 0, // Valor bruto en metros o metros cuadrados
      isHidden: false,
      map: null
    }
  },
  components:{
    MapToolHeader
  },
  computed: {
    isActive: function() {
      return this.isFixed? this.isMobile : this.mapToolValues('measures').active && !this.isMobile
    },
    availableUnits() {
      if (this.mode.code === 'distance' || this.mode.code === 'radius') {
        return [
          { label: 'Millas', value: 'mi' },
          { label: 'Kilómetros', value: 'km' },
          { label: 'Pies', value: 'ft' },
          { label: 'Metros', value: 'm' },
          { label: 'Yardas', value: 'yd' },
          { label: 'Millas náuticas', value: 'nmi' }
        ];
      } else if (this.mode.code === 'area') {
        return [
          { label: 'Acres', value: 'ac' },
          { label: 'Millas cuadradas', value: 'mi2' },
          { label: 'Kilómetros cuadrados', value: 'km2' },
          { label: 'Hectáreas', value: 'ha' },
          { label: 'Yardas cuadradas', value: 'yd2' },
          { label: 'Pies cuadrados', value: 'ft2' },
          { label: 'Metros cuadrados', value: 'm2' }
        ];
      }
      return [];
    },
    formattedResult() {
      if (!this.rawMeasureValue) return '';
      
      const val = this.getConvertedValue(this.rawMeasureValue, this.selectedUnit, this.mode.code);
      return val.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + this.selectedUnit;
    },
    // Solo en modo radio: área del círculo (πr²) a partir del mismo radio que ya
    // reporta measureRadius.js, igual que el tooltip del mapa (formatRadius).
    formattedArea() {
      if (this.mode.code !== 'radius' || !this.rawMeasureValue) return '';

      const unit = RADIUS_AREA_UNITS[this.selectedUnit] || 'm2';
      const area = Math.PI * this.rawMeasureValue * this.rawMeasureValue;
      const val = this.getConvertedValue(area, unit, 'area');
      return val.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + unit;
    }
  },
  methods:{
    setMode(code) {
      console.log('[Measure] Cambiando a modo:', code);
      if (this.mode.code === code) return;
      
      this.mode = this.options.find(o => o.code === code);
      
      // Ajustar unidad por defecto
      if (code === 'area') this.selectedUnit = 'm2';
      else this.selectedUnit = 'm';
      
      this.rawMeasureValue = 0;
      if (code === 'coords') this.coordFormat = 'GMS';
      this.selectOption();
    },
    // coordCell/coordZone: formatean fixedPoint/pointerPoint (measureLocation.js)
    // según coordFormat — index 0/1 son la primera/segunda columna de la
    // tabla (Latitud/Longitud en GMS y Decimal; Este/Norte en UTM, mismo
    // orden que la pestaña UTM del Localizador).
    coordCell(point, index) {
      if (!point) return '-';
      if (this.coordFormat === 'UTM') {
        if (!point.utm) return '-';
        return (index === 0 ? point.utm.x : point.utm.y).toFixed(0);
      }
      if (this.coordFormat === 'GMS') return index === 0 ? point.dms.lat : point.dms.lon;
      return (index === 0 ? point.lat : point.lon).toFixed(6);
    },
    coordZone(point) {
      return point && point.utm ? `${point.utm.zone}${point.utm.hemisferio}` : '-';
    },
    updateRawValue(value) {
      this.rawMeasureValue = value;
    },
    selectOption: function() {
      // Limpiar interacción actual Y el dibujo previo al cambiar de modo
      this.cleanAllInteractions(true);
      
      // Activar nueva interacción (solo si hay un modo válido seleccionado)
      if (this.mode && this.mode.code) {
        this.activateInteraction(this.mode.code);
      }
    },
    cleanMode: function() {
      this.rawMeasureValue = 0;
      if (!this.mode || !this.mode.code) {
        this.cleanAllInteractions(true);
        return;
      }
      
      // Limpiar solo la medición actual
      if (this.mode.code === 'distance') this.cleanMeasureDistance();
      if (this.mode.code === 'area') this.cleanMeasureArea();
      if (this.mode.code === 'radius') this.cleanMeasureRadius();
      if (this.mode.code === 'coords') this.cleanMeasureLocation();
    },
    exitMeasure: function() {
      // Limpiar todo y salir
      this.cleanAllInteractions(true);
      
      this.selectMapTool('measures', false)
      
      // Resetear modo al salir para la próxima vez
      this.mode = this.options[0];
    },
    visibilityChange: function(code, isVisible) {
      if (code === 'distance') this.setMeasureDistanceVisibility(isVisible);
      if (code === 'area') this.setMeasureAreaVisibility(isVisible);
    },
    
    // Helpers
    cleanAllInteractions(deleteDrawing) {
      if (deleteDrawing) this.rawMeasureValue = 0;
      this.removeMeasureDistanceInteraction(deleteDrawing);
      this.removeMeasureAreaInteraction(deleteDrawing);
      this.removeMeasureRadiusInteraction(deleteDrawing);
      this.removeMeasureLocationInteraction(deleteDrawing);
    },
    activateInteraction(code) {
      if (!code) return;
      
      this.map = mapService.get(); // Asegurar map actualizado
      if (code === 'distance') this.addMeasureDistanceInteraction();
      if (code === 'area') this.addMeasureAreaInteraction();
      if (code === 'radius') this.addMeasureRadiusInteraction();
      if (code === 'coords') this.addMeasureLocationInteraction();
    },

    getConvertedValue(val, unit, mode) {
      let result = val;
      
      // Lógica para Distancia / Radio (Base: metros)
      if (mode === 'distance' || mode === 'radius') {
        const factors = {
          'km': 1000,
          'mi': 1609.344,
          'ft': 0.3048,
          'yd': 0.9144,
          'nmi': 1852,
          'm': 1
        };
        if (factors[unit]) result = val / factors[unit];
      } 
      // Lógica para Área (Base: metros cuadrados)
      else if (mode === 'area') {
        const factors = {
          'km2': 1000000,
          'ha': 10000,
          'ac': 4046.856,
          'mi2': 2589988.11,
          'yd2': 0.836127,
          'ft2': 0.092903,
          'm2': 1
        };
        if (factors[unit]) result = val / factors[unit];
      }
      
      return result;
    },
  },
  updated () {

  },
  created () {

  },
  mounted () {
    this.makeDraggable("measureID");
    this.map = mapService.get();
  },
  beforeDestroy() {
    this.cleanAllInteractions(false);
  },
  watch: {
    // Acción que se ejecuta cuando se activa esta herramienta
    'mapToolSelected': function (newValue, oldValue) {
      if(newValue == 'measures') {
        if(this.isActive) {
           // Si ya hay un modo seleccionado (diferente al default), activarlo
           if (this.mode && this.mode.code) {
             this.activateInteraction(this.mode.code);
           }
        }
      } else {
        // Al deseleccionar, limpiar interacciones y dibujos
        this.cleanAllInteractions(true);
      }
    },
    'isActive': function(newVal) {
        if(newVal) {
            if (this.mode && this.mode.code) {
              this.activateInteraction(this.mode.code);
            }
        } else {
            this.cleanAllInteractions(true);
        }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.widgetContainer {
  position: absolute;
  top: 100px;
  left: 20px;
  width: 320px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  font-family: 'Inter', sans-serif;
}

.tool-body {
  padding: 20px;
  background: #fdfdfd;
}

.mode-selector {
  display: flex;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 20px;
}

.coords-results-container {
  padding: 10px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  margin-bottom: 20px;
}

.coords-table-wrapper {
  max-height: 120px;
  overflow-y: auto;
  overflow-x: auto;
  margin-top: 10px;
}

.coords-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th {
    text-align: center;
    color: #64748b;
    font-weight: 500;
    padding: 8px;
    border: none;
    font-size: 0.85rem;
  }

  td {
    padding: 4px 8px;
    color: #1e293b;
    border: none;
    text-align: center;
    font-family: 'Inter', sans-serif;
  }

  .coord-val {
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 0.5px;
  }
}

.mode-btn {
  flex: 1;
  background: white;
  border: none;
  border-right: 1px solid #e2e8f0;
  padding: 12px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.2rem;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: #f8fafc;
    color: #3282b8;
  }

  &.active {
    background: #e1f5fe;
    color: #0288d1;
    box-shadow: inset 0 0 0 2px #0288d1;
  }
}

.unit-selector {
  margin-bottom: 20px;
}

.custom-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  color: #334155;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #3282b8;
    box-shadow: 0 0 0 2px rgba(50, 130, 184, 0.1);
  }
}

.results-container {
  text-align: center;
  padding: 15px;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  margin-bottom: 20px;
}

.results-label {
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.results-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

/* Reusa el estilo del rotulo de seccion, en linea antes del valor */
.results-value .results-label {
  margin-right: 6px;
}

.results-area {
  margin-top: 6px;
  font-size: 1rem;
  font-weight: 600;
  color: #475569;
}

.results-placeholder {
  font-style: italic;
  color: #cbd5e1;
  font-size: 0.9rem;
}

.actions-bar {
  display: flex;
  justify-content: center;
}

.btn-clean {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
}
</style>
